import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Container,
  Breadcrumbs,
  Link,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Avatar,
  Divider,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Stack,
  Chip,
} from "@mui/material";
import {
  NavigateNext as NavigateNextIcon,
  Send as SendIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  LiveHelp as LiveHelpIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { getData, postFileUpload } from "../../Webservice/Webservice";
import { getToken } from "../../Utils/authHelper";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const THEME_COLOR = "#C8A054";

function TicketDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [replyPreviews, setReplyPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  const [callbackLoading, setCallbackLoading] = useState(false);
  const [callbackRequested, setCallbackRequested] = useState(false);

  const rawToken = getToken();
  const token = rawToken ? `Bearer ${rawToken}` : "";
  const customerId = sessionStorage.getItem("customerId") || localStorage.getItem("customerId");

  useEffect(() => {
    if (!rawToken) {
      navigate("/signin");
      return;
    }
    fetchTicketData();
  }, [id, rawToken]);



  const fetchTicketData = async () => {
    try {
      const res = await getData(`query/view/${id}`, null, token, false);
      if (res && res.success) {
        setTicket(res.query);
        setMessages(res.messages);
      }
    } catch (error) {
      console.error("Error fetching ticket data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + replyImages.length > 3) {
      alert("Max 3 images per reply.");
      return;
    }
    setReplyImages((prev) => [...prev, ...files]);
    const previews = files.map(file => URL.createObjectURL(file));
    setReplyPreviews((prev) => [...prev, ...previews]);
  };

  const removeImage = (index) => {
    setReplyImages((prev) => prev.filter((_, i) => i !== index));
    setReplyPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() && replyImages.length === 0) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("queryId", id);
      formData.append("message", reply);
      replyImages.forEach(img => formData.append("images", img));

      const res = await postFileUpload("query/reply", formData, token);
      if (res) {
        setReply("");
        setReplyImages([]);
        setReplyPreviews([]);
        fetchTicketData();
      }
    } catch (error) {
      console.error("Error sending reply:", error);
    } finally {
      setSending(false);
    }
  };

  const handleRequestCallback = async () => {
    if (callbackRequested || ticket.status === "resolved") return;

    setCallbackLoading(true);
    try {
      const formData = new FormData();
      formData.append("queryId", id);
      formData.append("message", "👋 [SYSTEM] Customer has requested a priority callback on their registered mobile number.");

      const res = await postFileUpload("query/reply", formData, token);
      if (res) {
        setCallbackRequested(true);
        fetchTicketData();
      }
    } catch (error) {
      console.error("Error requesting callback:", error);
    } finally {
      setCallbackLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={40} sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Container sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5">Ticket not found.</Typography>
        <Button onClick={() => navigate("/support")} sx={{ mt: 2, color: THEME_COLOR }}>Back to Support</Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: "#F8F9FA", minHeight: "100vh", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Navigation & Header */}
        <Box sx={{ mb: 4, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ opacity: 0.5 }} />}
            sx={{ mb: 1 }}
          >
            <Link underline="hover" color="inherit" onClick={() => navigate("/")} sx={{ cursor: "pointer", fontSize: "14px", fontWeight: 500, opacity: 0.7 }}>HOME</Link>
            <Link underline="hover" color="inherit" onClick={() => navigate("/support")} sx={{ cursor: "pointer", fontSize: "14px", fontWeight: 500, opacity: 0.7 }}>HELP CENTER</Link>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: THEME_COLOR }}>#{ticket.referenceNo}</Typography>
          </Breadcrumbs>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/support")}
              sx={{
                borderRadius: "30px",
                textTransform: "none",
                borderColor: "#ddd",
                color: "#666",
                px: 3,
                "&:hover": { borderColor: THEME_COLOR, color: THEME_COLOR }
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Main Chat Flow - 70% Width */}
          <Grid item xs={12} md={8.4}>
            <Paper elevation={0} sx={{
              borderRadius: "24px",
              border: "1px solid #E9ECEF",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
              mb: 4
            }}>
              {/* Chat Header */}
              <Box sx={{ p: 3, borderBottom: "1px solid #F1F3F5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar sx={{ bgcolor: THEME_COLOR, width: 48, height: 48 }}>
                    <LiveHelpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, ...rubikfont, fontSize: "18px" }}>Conversation with Support</Typography>
                    <Typography sx={{ fontSize: "12px", color: "green", display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, bgcolor: "green", borderRadius: "50%", animation: "pulse 2s infinite" }} />
                      Active Support Session
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Messages Area - Static Flow */}
              <Box sx={{
                p: 3,
                bgcolor: "#FDFDFD",
                display: "flex",
                flexDirection: "column",
                gap: 3
              }}>
                {/* Initial Query Bubble */}
                <Box sx={{ display: "flex", mb: 2 }}>
                  <Box sx={{ maxWidth: "85%", bgcolor: "#fff", p: 3, borderRadius: "0 20px 20px 20px", border: "1px solid #E9ECEF", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                    <Typography sx={{ fontWeight: 700, mb: 1, color: THEME_COLOR }}>Initial Query</Typography>
                    <Typography sx={{ fontSize: "15px", color: "#444", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                      {ticket.description}
                    </Typography>
                    {ticket.images?.length > 0 && (
                      <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {ticket.images.map((img, i) => (
                          <Box
                            key={i}
                            component="img"
                            src={img}
                            onClick={() => window.open(img, '_blank')}
                            sx={{ width: 80, height: 80, borderRadius: "12px", objectFit: "cover", cursor: "zoom-in", border: "1px solid #eee", transition: "0.2s", "&:hover": { transform: "scale(1.05)" } }}
                          />
                        ))}
                      </Box>
                    )}
                    <Typography sx={{ mt: 2, fontSize: "11px", color: "#999" }}>
                      Submitted on {new Date(ticket.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2, opacity: 0.5 }}><Chip label="Conversation History" size="small" variant="outlined" sx={{ fontSize: "10px", color: "#aaa" }} /></Divider>

                {messages.map((msg, idx) => {
                  const isCustomer = msg.senderRole === "Customer";
                  return (
                    <Box key={msg._id || idx} sx={{ display: "flex", flexDirection: "column", alignItems: isCustomer ? "flex-end" : "flex-start" }}>
                      <Box sx={{
                        maxWidth: "85%",
                        p: 2.5,
                        borderRadius: isCustomer ? "20px 20px 0 20px" : "0 20px 20px 20px",
                        bgcolor: isCustomer ? THEME_COLOR : "#fff",
                        color: isCustomer ? "#fff" : "#333",
                        border: isCustomer ? "none" : "1px solid #E9ECEF",
                        boxShadow: isCustomer ? "0 4px 15px rgba(200, 160, 84, 0.2)" : "0 2px 10px rgba(0,0,0,0.02)"
                      }}>
                        <Typography sx={{ fontSize: "15px", lineHeight: 1.6 }}>{msg.message}</Typography>
                        {msg.images?.length > 0 && (
                          <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {msg.images.map((img, i) => (
                              <Box
                                key={i}
                                component="img"
                                src={img}
                                onClick={() => window.open(img, '_blank')}
                                sx={{ width: 100, height: 100, borderRadius: "10px", objectFit: "cover", bgcolor: "#000", border: isCustomer ? "2px solid rgba(255,255,255,0.3)" : "1px solid #eee" }}
                              />
                            ))}
                          </Box>
                        )}
                        <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", alignItems: "center", opacity: 0.7 }}>
                          <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>{isCustomer ? "You" : "Support Team"}</Typography>
                          <Typography sx={{ fontSize: "10px" }}>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Reply Section */}
              {ticket.status !== "resolved" ? (
                <Box sx={{ p: 3, borderTop: "1px solid #F1F3F5", bgcolor: "#fff" }}>
                  <form onSubmit={handleReply}>
                    <Box sx={{ bgcolor: "#F8F9FA", borderRadius: "20px", p: 1, transition: "0.3s", "&:focus-within": { bgcolor: "#fff", boxShadow: "0 0 0 2px " + THEME_COLOR + "44" } }}>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Share more details or reply to support..."
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "& textarea": { fontSize: "15px", py: 1, px: 1.5 }
                        }}
                      />

                      <Box sx={{ p: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: replyPreviews.length > 0 ? "1px solid #eee" : "none" }}>
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                          {replyPreviews.map((src, i) => (
                            <Box key={i} sx={{ position: "relative", width: 50, height: 50 }}>
                              <img src={src} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "10px" }} />
                              <IconButton size="small" onClick={() => removeImage(i)} sx={{ position: "absolute", top: -8, right: -8, bgcolor: "#fff", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", p: 0.2 }}>
                                <CloseIcon sx={{ fontSize: 13, color: "red" }} />
                              </IconButton>
                            </Box>
                          ))}
                          {replyPreviews.length < 3 && (
                            <IconButton component="label" sx={{ color: "#666", "&:hover": { color: THEME_COLOR } }}>
                              <CloudUploadIcon />
                              <input type="file" hidden multiple accept="image/*" onChange={handleImageChange} />
                            </IconButton>
                          )}
                        </Box>
                        <Button
                          type="submit"
                          disabled={sending || (!reply.trim() && replyImages.length === 0)}
                          sx={{
                            bgcolor: THEME_COLOR,
                            color: "#fff",
                            borderRadius: "30px",
                            px: 4,
                            py: 1,
                            fontWeight: 700,
                            textTransform: "none",
                            boxShadow: "0 10px 20px " + THEME_COLOR + "44",
                            "&:hover": { bgcolor: "#b08a46", boxShadow: "none" },
                            "&.Mui-disabled": { bgcolor: "#E9ECEF", color: "#ADB5BD" }
                          }}
                        >
                          {sending ? <CircularProgress size={20} color="inherit" /> : "Send Now"}
                        </Button>
                      </Box>
                    </Box>
                  </form>
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: "center", bgcolor: "#F8FFF9", color: "#2B8A3E" }}>
                  <Typography sx={{ fontWeight: 700 }}>Support session has ended.</Typography>
                  <Typography sx={{ fontSize: "14px", opacity: 0.8 }}>This ticket is marked as resolved and is now read-only.</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Ticket Information Sidebar - Professional Sticky Sidebar */}
          <Grid item xs={12} md={3.6}>
            <Box sx={{ position: { md: "sticky", xs: "static" }, top: "110px" }}>
              <Stack spacing={3}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: "24px", border: "1px solid #E9ECEF" }}>
                  <Typography sx={{ fontWeight: 700, mb: 3, ...rubikfont, fontSize: "18px" }}>Ticket Status</Typography>
                  <Stepper
                    activeStep={ticket.status === "open" ? 0 : ticket.status === "in-progress" ? 1 : 2}
                    orientation="vertical"
                    sx={{
                      "& .MuiStepIcon-root.Mui-active": { color: THEME_COLOR },
                      "& .MuiStepIcon-root.Mui-completed": { color: "#4caf50" },
                      "& .MuiStepLabel-label": { fontWeight: 600, fontSize: "14px" }
                    }}
                  >
                    <Step><StepLabel>Ticket Raised</StepLabel></Step>
                    <Step><StepLabel>Working on it</StepLabel></Step>
                    <Step><StepLabel>Issue Resolved</StepLabel></Step>
                  </Stepper>

                  <Box sx={{ mt: 4, p: 2, bgcolor: "#F8F9FA", borderRadius: "16px" }}>
                    <Typography sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>CURRENT STATUS</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ px: 2, py: 0.5, borderRadius: "20px", fontSize: "12px", fontWeight: 800, textTransform: "uppercase", bgcolor: ticket.status === "open" ? "#E3F2FD" : ticket.status === "in-progress" ? "#FFF3E0" : "#E8F5E9", color: ticket.status === "open" ? "#2196F3" : ticket.status === "in-progress" ? "#F59E0B" : "#2B8A3E" }}>
                        {ticket.status}
                      </Box>
                    </Box>
                  </Box>
                </Paper>

                <Paper elevation={0} sx={{ p: 3, borderRadius: "24px", border: "1px solid #E9ECEF" }}>
                  <Typography sx={{ fontWeight: 700, mb: 3, ...rubikfont, fontSize: "18px" }}>Ticket Details</Typography>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography sx={{ fontSize: "11px", color: "#ADB5BD", fontWeight: 700, letterSpacing: "0.5px" }}>TICKET REFERENCE</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>#{ticket.referenceNo}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "11px", color: "#ADB5BD", fontWeight: 700, letterSpacing: "0.5px" }}>ISSUE SUBJECT</Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>{ticket.subject}</Typography>
                    </Box>
                    {ticket.orderId && (
                      <Box>
                        <Typography sx={{ fontSize: "11px", color: "#ADB5BD", fontWeight: 700, letterSpacing: "0.5px" }}>ASSOCIATED ORDER</Typography>
                        <Typography
                          sx={{ fontWeight: 700, fontSize: "14px", color: THEME_COLOR, cursor: "pointer", textDecoration: "underline" }}
                          onClick={() => navigate(`/orderdetails/${ticket.orderId._id || ticket.orderId}`)}
                        >
                          #{ticket.orderId.orderNumber || "View Details"}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography sx={{ fontSize: "11px", color: "#ADB5BD", fontWeight: 700, letterSpacing: "0.5px" }}>LAST UPDATE</Typography>
                      <Typography sx={{ fontSize: "14px" }}>{new Date(ticket.updatedAt || ticket.createdAt).toLocaleDateString()}</Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* <Box sx={{ p: 3, borderRadius: "24px", bgcolor: "#343A40", color: "#fff", position: "relative", overflow: "hidden" }}>
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Premium Support</Typography>
                  <Typography sx={{ fontSize: "13px", opacity: 0.8, mb: 2 }}>Need faster resolution? Our team usually responds within 2-4 business hours.</Typography>
                  <Button
                    fullWidth
                    disabled={callbackLoading || callbackRequested || ticket.status === "resolved"}
                    onClick={handleRequestCallback}
                    sx={{
                      bgcolor: callbackRequested ? "#2B8A3E" : THEME_COLOR,
                      color: "#fff",
                      borderRadius: "10px",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { bgcolor: callbackRequested ? "#2B8A3E" : "#b08a46" },
                      "&.Mui-disabled": { bgcolor: callbackRequested ? "#2B8A3E" : "#eee", color: callbackRequested ? "#fff" : "#888" }
                    }}
                  >
                    {callbackLoading ? <CircularProgress size={20} color="inherit" /> : callbackRequested ? "Callback Requested ✓" : "Request Callback"}
                  </Button>
                </Box>
                <LiveHelpIcon sx={{ position: "absolute", right: -20, bottom: -20, fontSize: "120px", opacity: 0.05, transform: "rotate(-15deg)" }} />
              </Box> */}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <style>{`
        @keyframes pulse {
            0% { transform: scale(0.95); opacity: 0.7; }
            70% { transform: scale(1.1); opacity: 0.3; }
            100% { transform: scale(0.95); opacity: 0.7; }
        }
      `}</style>
    </Box>
  );
}

export default TicketDetails;
